import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, catchError, filter, firstValueFrom, map, of, switchMap, take, tap, timeout } from 'rxjs';
import { FEATURE_FLAG_FACADE_TIMEOUT_IN_MS } from 'src/app/core/constants/feature-flag.constants';
import { FeatureFlagSelectors } from 'src/app/core/state/selectors/feature-flag.selectors';
import { FeatureFlagActions } from 'src/app/core/state/slices/feature-flag/feature-flag.actions';
import { simpleHash } from 'src/app/shared/utilities/simple-hash';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagFacade {
  constructor(private store: Store) {}

  /**
   * Fetches the feature flag from the server and returns a Promise containing a key-value pair object
   * of the feature flag keys and their Enabled state. If encounters unexpected failure it just returns
   * all flags as set to false, hence no try / catch block around this method is not required.
   *
   * The purpose of this function is for backwards compatibility with the previous version.
   *
   * @example -
   * const featureFlags = await this.featureFlagFacade.isFeatureEnabled([
   *  'flag_1',
   *  'flag_2',
   *  FeatureFlagEnum.EXAMPLE_FLAG
   * ]);
   *
   * The feature flag statuses can then be accessed by:
   *
   * if(featureFlags.flag_1) { ... }
   * else if(featureFlags.flag_2) { ... }
   * else if(featureFlags.example_flag) { ... }
   * @param featureKeys - An array of feature flag keys.
   * @param attributes - The feature flag attributes associated with the keys.
   * @param source - The source where the feature flag request is called.
   * @returns A Promise containing key-value pair of feature flag key and its enabled state.
   */
  async isFeatureEnabled<K extends string>(
    featureKeys: K[],
    attributes = {},
    source = 'FeatureFlagFacade.isFeatureEnabled',
  ): Promise<Record<K, boolean>> {
    return firstValueFrom(this.isFeatureEnabled$(featureKeys, attributes, source));
  }

  /**
   * Fetches the feature flag from the server and returns an Observable containing a key-value pair object
   * of the feature flag keys and their Enabled state.
   *
   * @param featureKeys - An array of feature flag keys.
   * @param attributes - The feature flag attributes associated with the keys.
   * @param source - The source where the feature flag request is called.
   * @returns An Observable containing key-value pair of feature flag key and its enabled state.
   */
  isFeatureEnabled$<K extends string>(
    featureKeys: K[],
    attributes = {},
    source = 'FeatureFlagFacade.isFeatureEnabled',
  ): Observable<Record<K, boolean>> {
    const uniqueFeatureKeys = Array.from(new Set(featureKeys));
    const featureFlagKeysHash = simpleHash(uniqueFeatureKeys.sort().join() + JSON.stringify(attributes));

    return this.store
      .select(FeatureFlagSelectors.selectCanRequestFeatureFlags(featureFlagKeysHash, uniqueFeatureKeys, attributes))
      .pipe(
        // Failsafe mechanism to handle unexpected (unknown) errors which lead to Observable emission failures.
        timeout(FEATURE_FLAG_FACADE_TIMEOUT_IN_MS),
        tap((canRequestFeatureFlags) => {
          if (canRequestFeatureFlags) {
            this.store.dispatch(
              FeatureFlagActions.fetchFeatureFlags({
                trigger: source,
                featureKeys: uniqueFeatureKeys,
                attributes,
                featureFlagKeysHash,
              }),
            );
          }
        }),
        switchMap(() => this.store.select(FeatureFlagSelectors.selectFeatureFlagsByKey(uniqueFeatureKeys, attributes))),

        // only emit if the number of flags matches the number of objects props
        filter((featureFlags) => Object.keys(featureFlags).length === uniqueFeatureKeys.length),

        // send the resolved feature flags from the store
        map((featureFlags) => featureFlags),

        // kill on first emission
        take(1),

        // Log error and rethrow for subscribers to handle.
        catchError((error) => {
          console.error(error, {
            module: 'FeatureFlagFacade.isFeatureEnabled',
            featureKeys,
            attributes,
          });

          return of(
            featureKeys.reduce(
              (returnedFeatureFlags, currentFeatureFlag) => ({
                ...returnedFeatureFlags,
                [currentFeatureFlag]: false,
              }),
              {} as Record<K, boolean>,
            ),
          );
        }),
      );
  }
}
